import React, { useMemo, useState } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { Alert, Tooltip } from 'antd';
import { Card, Tabs, TabList, Tab, TabPanels, TabPanel } from '../../design-system';
import {
  AutoCombinationKey,
  AutoCombinationSet,
  Curves,
  Zone
} from '../../@types/projects/types';
import {
  DEFAULT_GRAD_DIFF,
  useAutoCorrelation
} from '../../providers/AutoCorrelation';
import { useSelected } from '../../providers/selectedContext';
import {
  buildLambdaZoneCurves,
  buildLiaoZoneCurves
} from '../utils/curveGeneration';
import { Chart } from '../chart/BaseChart';
import { ChartSpec } from '../chart/chartSpec';
import { flagMessages } from './autoCorrelationUtils';
import styles from './AutoCorrelation.module.css';

const CURVE_POINTS = 50;

const COMBINATION_LABELS: Record<AutoCombinationKey, string> = {
  recommended: 'Recommended',
  best_r_squared: 'Best R²',
  current: 'Current'
};

const FALLBACK_ZONE_COLORS = [
  '#72E2D8',
  '#4D7CD6',
  '#D4A106',
  '#D4380D',
  '#9254DE',
  '#389E0D'
];

interface ZonePreview {
  uid: string;
  label: string;
  color: string;
  invalid: boolean;
  unavailable: boolean;
  tooltip: string;
  curves: Curves | null;
  missingInputs: string[];
}

type PcMethodology = 'liao' | 'lambda';

const METHODOLOGY_LABELS: Record<PcMethodology, string> = {
  liao: 'Liao',
  lambda: 'Brooks-Corey'
};

const isFlagForMethod = (
  flag: AutoCombinationSet['curve_flags'][string][number],
  methodology: PcMethodology
) => {
  const analysis = flag.context?.analysis;
  return !analysis || analysis === 'both' || analysis === methodology;
};

const SHARED_CURVE_INPUTS = [
  'swir',
  'true_pc_max',
  'nw',
  'no_w',
  'ng',
  'krw_absolute',
  'kro_absolute',
  'krg_absolute',
  'sor_w',
  'sor_g',
  'sgc'
] as const;

const METHODOLOGY_CURVE_INPUTS: Record<PcMethodology, readonly string[]> = {
  liao: ['best_a', 'best_b', 'pce', 'lambda_variable'],
  lambda: ['pe', 'lambda_value']
};

const missingCurveInputs = (
  combination: AutoCombinationSet,
  zoneUid: string,
  methodology: PcMethodology,
  gradDiff: number
): string[] => {
  const values = combination.zone_values[zoneUid]?.[
    methodology === 'liao' ? 'liao_values' : 'lambda_values'
  ] as Record<string, unknown> | null | undefined;
  if (!values) return [`${METHODOLOGY_LABELS[methodology]} zone values`];

  const missing = [
    ...SHARED_CURVE_INPUTS,
    ...METHODOLOGY_CURVE_INPUTS[methodology]
  ].filter(
    name => typeof values[name] !== 'number' || !Number.isFinite(values[name])
  );
  // Legacy reports shared the Water-Oil Kro endpoint. New reports must carry
  // a separate Gas-Oil endpoint, but remain renderable through this fallback.
  if (
    (typeof values.kro_absolute_g !== 'number' ||
      !Number.isFinite(values.kro_absolute_g)) &&
    (typeof values.kro_absolute !== 'number' ||
      !Number.isFinite(values.kro_absolute))
  ) {
    missing.push('kro_absolute_g');
  }
  if (!Number.isFinite(gradDiff)) missing.push('gradient difference');
  return missing;
};

const buildZonePreviews = (
  combination: AutoCombinationSet,
  zones: Zone[],
  gradDiff: number,
  methodology: PcMethodology
): ZonePreview[] =>
  Object.keys(combination.zone_values).map((zoneUid, index) => {
    const projectZone = zones.find(zone => zone.uid === zoneUid);
    const zoneIndex = projectZone ? zones.indexOf(projectZone) : index;
    const zoneForCurves: Zone = {
      uid: zoneUid,
      start: projectZone?.start ?? 0,
      end: projectZone?.end ?? 0,
      mean: projectZone?.mean ?? 0,
      color: projectZone?.color ?? null,
      zone_values: combination.zone_values[zoneUid],
      zone_curves: null
    };
    const curves = methodology === 'liao'
      ? buildLiaoZoneCurves(zoneForCurves, gradDiff, CURVE_POINTS)
      : buildLambdaZoneCurves(zoneForCurves, gradDiff, CURVE_POINTS);
    const flags = (combination.curve_flags[zoneUid] ?? []).filter(flag =>
      isFlagForMethod(flag, methodology)
    );
    const missingInputs = missingCurveInputs(
      combination,
      zoneUid,
      methodology,
      gradDiff
    );
    const invalid = flags.some(flag => flag.severity === 'reject');
    return {
      uid: zoneUid,
      label: `Rock Type ${zoneIndex + 1}`,
      color:
        projectZone?.color ??
        FALLBACK_ZONE_COLORS[zoneIndex % FALLBACK_ZONE_COLORS.length],
      invalid,
      unavailable: missingInputs.length > 0 || curves === null,
      tooltip:
        missingInputs.length > 0
          ? `Missing prerequisites: ${missingInputs.join(', ')}`
          : flags.length > 0
            ? flagMessages(flags)
            : curves
              ? 'Curve generated without validation flags'
              : 'Curve could not be generated',
      curves,
      missingInputs
    };
  });

const lineSeries = (
  name: string,
  color: string,
  xValues: number[],
  yValues: number[],
  dashed = false
): SeriesOptionsType => ({
  name,
  type: 'line',
  color,
  dashStyle: dashed ? 'Dash' : 'Solid',
  marker: { enabled: false },
  data: xValues.map((x, index) => [x, yValues[index]])
});

const PreviewChart = ({
  title,
  xTitle,
  yTitle,
  series
}: {
  title: string;
  xTitle: string;
  yTitle: string;
  series: SeriesOptionsType[];
}) => {
  const spec: ChartSpec = {
    title,
    height: 240,
    legend: { enabled: false },
    exportEnabled: false,
    xAxis: { title: xTitle },
    yAxis: { title: yTitle },
    series
  };
  return (
    <div className={styles.previewCell}>
      <Chart spec={spec} />
    </div>
  );
};

const CombinationPanel = ({
  combination,
  gradDiff,
  methodology
}: {
  combination: AutoCombinationSet;
  gradDiff: number;
  methodology: PcMethodology;
}) => {
  const { selectedInfo } = useSelected();
  const { report } = useAutoCorrelation();
  const zones = selectedInfo.project?.correlation?.zones ?? [];

  const previews = useMemo(
    () => buildZonePreviews(combination, zones, gradDiff, methodology),
    [combination, zones, gradDiff, methodology]
  );

  const charts = useMemo(() => {
    const pc: SeriesOptionsType[] = [];
    const height: SeriesOptionsType[] = [];
    const krSw: SeriesOptionsType[] = [];
    const krSg: SeriesOptionsType[] = [];
    previews.forEach(preview => {
      if (!preview.curves) return;
      const { sw, pcow, height: h, krw, krow, sg, krg, krog } = preview.curves;
      pc.push(lineSeries(preview.label, preview.color, sw, pcow));
      height.push(lineSeries(preview.label, preview.color, sw, h));
      krSw.push(
        lineSeries(`${preview.label} Krw`, preview.color, sw, krw),
        lineSeries(`${preview.label} Krow`, preview.color, sw, krow, true)
      );
      krSg.push(
        lineSeries(`${preview.label} Krg`, preview.color, sg, krg),
        lineSeries(`${preview.label} Krog`, preview.color, sg, krog, true)
      );
    });
    return { pc, height, krSw, krSg };
  }, [previews]);

  const missingInputs = useMemo(
    () => Array.from(new Set(previews.flatMap(preview => preview.missingInputs))),
    [previews]
  );

  const combinationWarnings = combination.flags.filter(
    flag => flag.severity !== 'info'
  );
  const hasCurves = charts.pc.length > 0;

  return (
    <div>
      <p className={styles.assistedHint}>
        Uses the shared Kr source:{' '}
        {(report?.scope.endpoint_source ?? 'lab') === 'refinement'
          ? 'Refinement values'
          : 'Lab values'}.
      </p>
      {combinationWarnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ margin: '0.5rem 0' }}
          message={combinationWarnings.map(flag => flag.message).join(' · ')}
        />
      ) : null}

      {previews.length > 0 ? (
        <div className={styles.zoneChips}>
          {previews.map(preview => (
            <Tooltip key={preview.uid} title={preview.tooltip}>
              <span
                className={`${styles.zoneChip} ${
                  preview.invalid
                    ? styles.zoneChipInvalid
                    : preview.unavailable
                      ? styles.zoneChipUnavailable
                    : styles.zoneChipValid
                }`}
                data-testid={`auto-zone-chip-${preview.uid}`}
              >
                <span
                  className={styles.zoneChipColor}
                  style={{ backgroundColor: preview.color }}
                />
                {preview.label} —{' '}
                {preview.invalid
                  ? 'invalid'
                  : preview.unavailable
                    ? 'unavailable'
                    : 'valid'}
              </span>
            </Tooltip>
          ))}
        </div>
      ) : (
        <p className={styles.assistedHint}>
          No zone curves available for this combination.
        </p>
      )}

      {hasCurves ? (
        <div className={styles.previewGrid}>
          <PreviewChart
            title="Pc × Sw"
            xTitle="Sw"
            yTitle="Pc"
            series={charts.pc}
          />
          <PreviewChart
            title="Height × Sw"
            xTitle="Sw"
            yTitle="Height"
            series={charts.height}
          />
          <PreviewChart
            title="Kr × Sw"
            xTitle="Sw"
            yTitle="Kr"
            series={charts.krSw}
          />
          <PreviewChart
            title="Kr × Sg"
            xTitle="Sg"
            yTitle="Kr"
            series={charts.krSg}
          />
        </div>
      ) : (
        <Alert
          type="warning"
          showIcon
          message={`${METHODOLOGY_LABELS[methodology]} curves are unavailable`}
          description={
            missingInputs.length > 0
              ? `Missing prerequisites: ${missingInputs.join(', ')}.`
              : `The ${METHODOLOGY_LABELS[methodology]} zone values could not produce physically valid curves.`
          }
        />
      )}
    </div>
  );
};

/**
 * Combination comparison — one tab per combination set (Recommended /
 * Best R² / Current when present) with per-zone validity chips and
 * client-side curve previews built from the report's zone values.
 */
const CombinationPreview = () => {
  const { report, gradDiff, activeCombinationKey, setActiveCombinationKey } =
    useAutoCorrelation();
  const [activeMethodology, setActiveMethodology] = useState<PcMethodology>('liao');

  if (!report || report.combination_sets.length === 0) return null;

  const effectiveGradDiff =
    typeof report.grad_diff === 'number' && Number.isFinite(report.grad_diff)
      ? report.grad_diff
      : gradDiff ?? DEFAULT_GRAD_DIFF;

  const activeKey = report.combination_sets.some(
    combination => combination.key === activeCombinationKey
  )
    ? activeCombinationKey
    : report.combination_sets[0].key;

  const methodologies: PcMethodology[] = [];
  if (
    report.parameters.best_a?.recommended &&
    report.parameters.best_b?.recommended &&
    report.parameters.pce?.recommended
  ) {
    methodologies.push('liao');
  }
  if (
    report.parameters.pe?.recommended &&
    report.parameters.lambda_value?.recommended
  ) {
    methodologies.push('lambda');
  }
  const effectiveMethodology = methodologies.includes(activeMethodology)
    ? activeMethodology
    : methodologies[0];

  return (
    <Card variant="outlined" padding="md" fullWidth>
      <Card.Header
        title="Combination comparison"
        subtitle="Per-zone validity and simulated curves for each candidate selection"
      />
      <Card.Body>
        <Tabs
          value={activeKey}
          onChange={value =>
            setActiveCombinationKey(value as AutoCombinationKey)
          }
          variant="underline"
          size="sm"
        >
          <TabList>
            {report.combination_sets.map(combination => (
              <Tab key={combination.key} value={combination.key}>
                {COMBINATION_LABELS[combination.key]}
                {combination.valid ? '' : ' (invalid)'}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {report.combination_sets.map(combination => (
              <TabPanel key={combination.key} value={combination.key}>
                {effectiveMethodology ? (
                  <Tabs
                    value={effectiveMethodology}
                    onChange={value =>
                      setActiveMethodology(value as PcMethodology)
                    }
                    variant="underline"
                    size="sm"
                  >
                    <TabList>
                      {methodologies.map(methodology => (
                        <Tab key={methodology} value={methodology}>
                          {METHODOLOGY_LABELS[methodology]}
                        </Tab>
                      ))}
                    </TabList>
                    <TabPanels>
                      {methodologies.map(methodology => (
                        <TabPanel key={methodology} value={methodology}>
                          <CombinationPanel
                            combination={combination}
                            gradDiff={effectiveGradDiff}
                            methodology={methodology}
                          />
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="No capillary-pressure methodology is available for this combination."
                  />
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default CombinationPreview;
